import React from 'react';
import styled from 'styled-components';

import {
  Divider,
  Icon,
  Input,
  LabelWrapper,
  PronounTags,
  PronounTagsProps,
  PRONOUN_TAGS,
  theme,
} from 'components';

import { IAppointmentConfig } from '@bb/domain/dist/types';
import { useContactState, useContactModalState } from './hooks';
import { ImportanceType, PhoneType } from './types';
import {
  ContactImportanceSelect,
  EmailInput,
  EmailInputValueType,
  PhoneInput,
  PhoneInputValueType,
  RequiredInput,
  MustCallWarning,
} from './components';
import ContactSearch from './ContactSearch';
import ContactTypeSelect from './ContactTypeSelect';
import {
  StyledDetailInputsContainer,
  StyledDetailSectionContainer,
} from './styled';

type ContactInputsProps = {
  contacts?: IAppointmentConfig['contacts'];
  addBuyerContact?: boolean;
};

const ContactInputs: React.FC<ContactInputsProps> = ({
  contacts,
  addBuyerContact,
}) => {
  const [contactState, updateContactState, errors] = useContactState();
  const [contactModalState] = useContactModalState();

  const { importance, firstName, lastName, phone, pronoun, email, deliveries } =
    contactState;
  const { contactInputsDisabled, isMustCallDisabled } = contactModalState;

  const handleOnImportanceChange = (value: ImportanceType) => {
    updateContactState({ importance: value });
  };

  const createNameChangeHandler =
    (key: 'firstName' | 'lastName') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newName = e.target.value;
      updateContactState({ [key]: newName });
    };

  const pronounTags = pronoun.map(key => PRONOUN_TAGS[key]);
  const handleOnPronounChange: PronounTagsProps['onChange'] = options => {
    const pronounArray = options.map(({ value }) => value);

    updateContactState({ pronoun: pronounArray });
  };

  const handleOnEmailChange = (value: EmailInputValueType) => {
    updateContactState({
      email: value.email,
      deliveries: {
        Email: value.sendEmail,
      },
    });
  };

  const createPhoneChangeHandler =
    (index: number) => (value: PhoneInputValueType) => {
      const updatedPhone = contactState.phone.map((p, i) => {
        if (i === index) {
          // If it's the item to replace, apply the replace filter
          return { ...p, ...value, number: value.number.replace(/ x_/g, '') };
        }
        return p;
      });

      updateContactState({ phone: updatedPhone });
    };

  const addNewPhone = () => {
    updateContactState(prevState => {
      const updatedPhone = [
        ...prevState.phone,
        {
          mustCall: false,
          number: '',
          sendSms: false,
          type: 'Primary' as PhoneType,
        },
      ];

      return {
        ...prevState,
        phone: updatedPhone,
      };
    });
  };

  const removePhone = (index: number) => {
    updateContactState(prevState => {
      const prevPhone = prevState.phone;
      const updatedPhone = [
        ...prevPhone.slice(0, index),
        ...prevPhone.slice(index + 1),
      ];

      return { ...prevState, phone: updatedPhone };
    });
  };

  return (
    <StyledDetailSectionContainer>
      <ContactSearch contacts={contacts} addBuyerContact={addBuyerContact} />
      <StyledDivider plain>Or enter manually</StyledDivider>
      <StyledDetailInputsContainer>
        <ContactTypeSelect addBuyerContact={addBuyerContact} />
        <ContactImportanceSelect
          value={importance}
          onChange={handleOnImportanceChange}
          disabled={contactInputsDisabled || addBuyerContact}
        />
        <RequiredInput
          label="First Name"
          value={firstName}
          onChange={createNameChangeHandler('firstName')}
          disabled={contactInputsDisabled}
          error={errors.firstName}
          data-cy="edit-contact-input-firstname"
        />
        <Input
          label="Last Name"
          value={lastName}
          onChange={createNameChangeHandler('lastName')}
          disabled={contactInputsDisabled}
          data-cy="edit-contact-input-lastname"
        />
        <LabelWrapper label="Pronoun" dataCy={'edit-contact-add-pronoun'}>
          <PronounTags
            onChange={handleOnPronounChange}
            tags={pronounTags}
            disabled={contactInputsDisabled}
          />
        </LabelWrapper>
      </StyledDetailInputsContainer>
      <EmailInput
        value={{
          email,
          sendEmail: deliveries.Email,
        }}
        onChange={handleOnEmailChange}
        disabled={contactInputsDisabled}
        error={errors?.email}
        dataCy="edit-contact-email"
      />
      {phone.map((phoneObj, index) => (
        <StyledPhoneInputContainer key={index}>
          <PhoneInput
            label={index === 0 ? 'Phone' : undefined}
            value={phoneObj}
            onChange={createPhoneChangeHandler(index)}
            disabled={contactInputsDisabled}
            showOptions={contactState.type !== 'Buyer'}
            placeholder={index > 0 ? 'Phone' : undefined}
            phoneNumberMask
            error={errors?.phone[index]}
            dataCy="edit-contact-input-phone"
          />
          {index > 0 && (
            <StyledIconContainer onClick={() => removePhone(index)}>
              <StyledCloseIcon />
            </StyledIconContainer>
          )}
        </StyledPhoneInputContainer>
      ))}
      <MustCallWarning />
      {phone.length < 3 && !contactInputsDisabled && (
        <StyledAddPhone onClick={addNewPhone} data-cy={'add-phone-button'}>
          + Add Phone
        </StyledAddPhone>
      )}
    </StyledDetailSectionContainer>
  );
};

export default ContactInputs;

const StyledPhoneInputContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 18px;
  gap: 8px;
`;

const StyledIconContainer = styled.div`
  padding-top: 4px;
`;

const StyledAddPhone = styled.div`
  cursor: pointer;
`;

const StyledDivider = styled(Divider)`
  && {
    color: ${theme.gray7};
    margin: 0;
    &::before,
    &::after {
      top: 0;
    }
  }
`;

const StyledCloseIcon = styled(Icon).attrs({
  type: 'antdCloseCircleOutlined',
})`
  cursor: pointer;
`;
